import { TagList } from '@/components/tags/TagList';
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { useTags } from '@/hooks/use-tags';
import { AppLayout } from '@/components/layout/AppLayout';

export default function TagsPage() {
  const { tags } = useTags();
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter tags based on search term
  const filteredTags = searchTerm
    ? tags.filter(tag => 
        tag.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : tags;

  return (
    <AppLayout>
    <div className="container mx-auto py-8 px-6">
      <h1 className="text-3xl font-bold mb-6">Manage Tags</h1>
      
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>
      
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div className="border rounded-lg p-6 bg-card">
            <TagList 
              title="All Tags" 
              showCreateButton={true}
              filterTags={filteredTags.map(tag => tag.name)}
            />
          </div>
          
          <div className="border rounded-lg p-6 bg-card">
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Tag Usage</h3>
              <p className="text-muted-foreground">
                Tags help you organize your content. Use the # symbol followed by a word to create a tag in your documents, notes, and quotes.
              </p>
              <div className="space-y-2">
                <p className="font-medium">Example:</p>
                <div className="bg-muted p-3 rounded-md">
                  <p>This is a note about React hooks #react #javascript</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="space-y-6">
          <div className="border rounded-lg p-6 bg-card">
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Tag Statistics</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="border rounded-md p-4 bg-background">
                  <p className="text-sm text-muted-foreground">Total Tags</p>
                  <p className="text-2xl font-bold">{tags.length}</p>
                </div>
                
                <div className="border rounded-md p-4 bg-background">
                  <p className="text-sm text-muted-foreground">Recent Tags</p>
                  <p className="text-2xl font-bold">
                    {tags.slice(0, 5).map(tag => (
                      <span 
                        key={tag.id} 
                        className="inline-block mr-1 px-1.5 rounded text-xs"
                        style={{ backgroundColor: tag.color || "#94a3b8", color: 'white' }}
                      >
                        #{tag.name}
                      </span>
                    ))}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border rounded-lg p-6 bg-card">
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Tips</h3>
              <ul className="space-y-2 list-disc list-inside text-muted-foreground">
                <li>Use descriptive, concise tag names</li>
                <li>Be consistent in your tag naming conventions</li>
                <li>Tags are automatically recognized as you type</li>
                <li>Click on a tag anywhere to view all content with that tag</li>
                <li>Edit or delete tags here to update them across all content</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
    </AppLayout>
  );
}